import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { date, money } from '../lib/format';
import { supabase } from '../lib/supabase';

interface PublicQuoteData {
  number: number;
  created_at: string;
  deadline: string | null;
  reference: string | null;
  hide_total: boolean;
  allow_comments: boolean;
  accept_online: boolean;
  accepted_at: string | null;
  accepted_by_name: string | null;
  status: string | null;
  subtotal: number;
  tax_total: number;
  total: number;
  customer: { name: string } | null;
  contact: { name: string | null; email: string | null; phone: string | null } | null;
  ship_to: { line1: string | null; line2: string | null; city: string | null; postcode: string | null } | null;
  prepared_by: { name: string; email: string } | null;
  org: { name: string; legal_name: string; email: string | null; phone: string | null; website: string | null;
         address: string | null; gst_number: string | null; quote_terms: string | null; payment_terms: string | null };
  items: { description: string | null; qty: number; unit_sell: number; total_sell: number; tax: string | null; decorations: string[] | null }[];
  comments: { body: string; created_at: string; from: string | null; is_staff: boolean }[];
}

/** The page a customer opens from the quote email. No sign-in; the link is the key. */
export default function PublicQuote() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const preview = params.get('preview') === '1';
  const [q, setQ] = useState<PublicQuoteData | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing'>('loading');

  const load = useCallback(async (track: boolean) => {
    const { data, error } = await supabase.rpc('public_quote', { p_token: token, p_track: track });
    if (error || !data) return setState('missing');
    setQ(data as PublicQuoteData);
    setState('ready');
  }, [token]);

  useEffect(() => { load(!preview); }, [load, preview]);

  useEffect(() => {
    if (q) document.title = `Quote ${q.number} · ${q.org.name}`;
  }, [q]);

  if (state === 'loading') return <div className="pq-wrap"><div className="empty">Loading your quote…</div></div>;
  if (state === 'missing' || !q) {
    return (
      <div className="pq-wrap">
        <div className="pq-sheet" style={{ textAlign: 'center' }}>
          <h1>Quote not found</h1>
          <p className="muted">This link may be out of date. Please contact Simply Totes on 09 886 3112 or hello@simplytotes.co.nz.</p>
        </div>
      </div>
    );
  }

  const addr = q.ship_to && [q.ship_to.line1, q.ship_to.line2, [q.ship_to.city, q.ship_to.postcode].filter(Boolean).join(' ')].filter(Boolean);

  return (
    <div className="pq-wrap">
      {preview && <div className="pq-banner no-print">Staff preview — viewing this does not mark the quote as viewed.</div>}
      <div className="pq-actions no-print">
        <button onClick={() => window.print()}>Download PDF</button>
      </div>

      <article className="pq-sheet">
        <header className="pq-head">
          <div>
            <div className="pq-brand">SIMPLY TOTES<span> + CO</span></div>
            <div className="pq-org small">
              {q.org.phone && <div>{q.org.phone}</div>}
              {q.org.email && <div>{q.org.email}</div>}
              {q.org.website && <div>{q.org.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</div>}
              {q.org.gst_number && <div>GST {q.org.gst_number}</div>}
            </div>
          </div>
          <div className="pq-title">
            <div className="pq-kicker">Quote</div>
            <div className="pq-num">#{q.number}</div>
            <div className="small muted">{date(q.created_at)}</div>
            {q.accepted_at && <div className="pq-accepted">Accepted</div>}
          </div>
        </header>

        <section className="pq-parties">
          <div>
            <div className="pq-label">Prepared for</div>
            {q.customer && <b>{q.customer.name}</b>}
            {q.contact?.name && <div>{q.contact.name}</div>}
            {addr?.map((l, i) => <div key={i}>{l}</div>)}
            {q.contact?.email && <div className="muted">{q.contact.email}</div>}
          </div>
          <div>
            <div className="pq-label">Prepared by</div>
            <b>{q.org.name}</b>
            {q.prepared_by?.name && <div>{q.prepared_by.name}</div>}
            {q.prepared_by?.email && <div className="muted">{q.prepared_by.email}</div>}
          </div>
          <div>
            {q.reference && <><div className="pq-label">Reference</div><div>{q.reference}</div></>}
            {q.deadline && <><div className="pq-label" style={{ marginTop: 8 }}>Needed by</div><div>{date(q.deadline)}</div></>}
          </div>
        </section>

        <table className="pq-lines">
          <thead>
            <tr><th>Item</th><th className="num">Qty</th>{!q.hide_total && <><th className="num">Rate</th><th className="num">Total</th></>}</tr>
          </thead>
          <tbody>
            {q.items.map((it, i) => (
              <tr key={i}>
                <td>
                  <div className="pq-desc">{it.description}</div>
                  {it.decorations?.map((d, j) => <div key={j} className="small muted">• {d}</div>)}
                </td>
                <td className="num">{Number(it.qty).toLocaleString('en-NZ')}</td>
                {!q.hide_total && <><td className="num">{money(it.unit_sell)}</td><td className="num">{money(it.total_sell)}</td></>}
              </tr>
            ))}
          </tbody>
        </table>

        {!q.hide_total && (
          <div className="pq-totals">
            <div><span>Subtotal</span><span>{money(q.subtotal)}</span></div>
            <div><span>GST 15%</span><span>{money(q.tax_total)}</span></div>
            <div className="grand"><span>Total NZD</span><span>{money(q.total)}</span></div>
          </div>
        )}

        {(q.org.quote_terms || q.org.payment_terms) && (
          <section className="pq-terms small">
            <div className="pq-label">Terms</div>
            {q.org.quote_terms && <p>{q.org.quote_terms}</p>}
            {q.org.payment_terms && <p>{q.org.payment_terms}</p>}
          </section>
        )}

        <Accept q={q} token={token} disabled={preview} onDone={() => load(false)} />
      </article>

      {q.allow_comments && <Comments q={q} token={token} disabled={preview} onDone={() => load(false)} />}
      <p className="pq-foot no-print">{q.org.legal_name} trading as {q.org.name}</p>
    </div>
  );
}

function Accept({ q, token, disabled, onDone }: { q: PublicQuoteData; token: string; disabled: boolean; onDone: () => void }) {
  const [name, setName] = useState(q.contact?.name ?? '');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (q.accepted_at) {
    return (
      <section className="pq-accept done">
        <b>Accepted by {q.accepted_by_name} on {date(q.accepted_at)}.</b>
        <div className="small">Thank you — we'll be in touch about artwork proofs and timing.</div>
      </section>
    );
  }
  if (!q.accept_online) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error } = await supabase.rpc('public_quote_accept', { p_token: token, p_name: name });
    setBusy(false);
    if (error) return setError(error.message);
    onDone();
  };

  return (
    <form className="pq-accept no-print" onSubmit={submit}>
      <div className="pq-label">Happy to go ahead?</div>
      <div className="row wrap">
        <input placeholder="Your full name" value={name} onChange={(e) => setName(e.target.value)} style={{ maxWidth: 280 }} />
        <label className="check small">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
          I accept this quote{q.org.quote_terms || q.org.payment_terms ? ' and its terms' : ''}
        </label>
        <button className="primary" disabled={disabled || busy || !agree || name.trim().length < 2}>
          {busy ? 'Accepting…' : `Accept quote${q.hide_total ? '' : ` · ${money(q.total)}`}`}
        </button>
      </div>
      {disabled && <div className="small muted">Accepting is turned off in staff preview.</div>}
      {error && <div className="error">{error}</div>}
    </form>
  );
}

function Comments({ q, token, disabled, onDone }: { q: PublicQuoteData; token: string; disabled: boolean; onDone: () => void }) {
  const [name, setName] = useState(q.contact?.name ?? '');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error } = await supabase.rpc('public_quote_comment', { p_token: token, p_name: name, p_body: body });
    setBusy(false);
    if (error) return setError(error.message);
    setBody('');
    setSent(true);
    onDone();
  };

  return (
    <section className="pq-sheet pq-comments no-print">
      <h2>Questions or changes?</h2>
      {q.comments.map((c, i) => (
        <div key={i} className={`pq-comment ${c.is_staff ? 'staff' : ''}`}>
          <div className="small muted">{c.from ?? 'Simply Totes'} · {date(c.created_at)}</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{c.body}</div>
        </div>
      ))}
      <form className="stack" onSubmit={submit} style={{ marginTop: 12 }}>
        <input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} style={{ maxWidth: 280 }} />
        <textarea placeholder="Write a message to Simply Totes" value={body} onChange={(e) => { setBody(e.target.value); setSent(false); }} />
        {error && <div className="error">{error}</div>}
        {sent && <div className="ok">Thanks — your message has been sent to Simply Totes.</div>}
        <div><button disabled={disabled || busy || !body.trim() || name.trim().length < 2}>Send message</button></div>
      </form>
    </section>
  );
}
